import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import {
  defaultHalcyonAuthorizationSourceOption,
  defaultHalcyonResubmitModeOption,
  getHalcyonSpecificYearOptions,
  halcyonAuthorizationSourceOptions,
  halcyonFormGroupBy,
  halcyonMethodOptions,
  HalcyonMultiYearEnum,
  halcyonResubmitModeOptions,
  halcyonYearModeOptions,
} from 'constants/loanActionEditor';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import {
  loadHalcyonFormCatalogAction,
  selectHalcyonFormPickerOptions,
} from 'store/loanActions/halcyonFormCatalog';

import { HalcyonTaxTranscriptFormTestIds } from './HalcyonTaxTranscriptForm.testIds';
import {
  checkIsPreAuthWithoutForms,
  getHalcyonTaxTranscriptSchema,
} from './HalcyonTaxTranscriptForm.utils';

const HalcyonTaxTranscriptForm = ({
  isLoading,
  setIsLoading,
  formRef,
  onSubmit,
  borrowerOptions,
  isPreAuthorization = false,
}) => {
  const hasBorrowerOptions = borrowerOptions?.length > 0;
  const {
    form,
    setSchema,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    validationErrors,
  } = useForm({
    resubmitMode: defaultHalcyonResubmitModeOption,
    authorizationSource: defaultHalcyonAuthorizationSourceOption,
  });

  const dispatch = useDispatch();
  const halcyonFormOptions = useSelector(selectHalcyonFormPickerOptions);

  const specificYearOptions = useMemo(() => getHalcyonSpecificYearOptions(), []);

  const isSpecificYears = form.yearMode?.value === HalcyonMultiYearEnum.SPECIFIC;
  const areFormsHidden = checkIsPreAuthWithoutForms(isPreAuthorization, form.method?.value);

  useEffect(() => {
    setSchema(
      getHalcyonTaxTranscriptSchema({ hasBorrowerOptions, areFormsHidden, isSpecificYears }),
    );
  }, [setSchema, hasBorrowerOptions, areFormsHidden, isSpecificYears]);

  useEffectSafeStrictMode(() => {
    dispatch(loadHalcyonFormCatalogAction());
  }, [dispatch]);

  const formsValue = form.forms || [];
  const specificYearsValue = form.specificYears || [];
  const borrowersValue = form.borrowers || [];

  const onSubmitForm = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      onSubmit({
        method: form.method?.value,
        forms: areFormsHidden
          ? []
          : (form.forms || []).map(f => ({
              formType: f.value,
              yearMode: form.yearMode?.value,
              ...(isSpecificYears
                ? { specificYears: (form.specificYears || []).map(y => y.value) }
                : {}),
            })),
        resubmitMode: form.resubmitMode?.value,
        authorizationSource: form.authorizationSource?.value,
        ...(hasBorrowerOptions ? { borrowerIds: (form.borrowers || []).map(b => b.value) } : {}),
      }).finally(() => setIsLoading(false));
    },
    [
      validateForm,
      form,
      onSubmit,
      isSpecificYears,
      areFormsHidden,
      hasBorrowerOptions,
      setIsLoading,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onGetOptionSelected = useCallback((option, value) => option.value === value?.value, []);

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="halcyon-tax-transcript-form"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      submitButtonName="Submit"
      onSubmit={onSubmitForm}
    >
      <AutocompleteCDS
        disableClearable
        required
        containerClassName="mb-24"
        dataTestId={HalcyonTaxTranscriptFormTestIds.TRANSCRIPT_METHOD_SELECT}
        disabled={isLoading}
        error={!!validationErrors.method}
        helperText={validationErrors.method}
        label="Transcript Method"
        name="method"
        options={halcyonMethodOptions}
        value={form.method || ''}
        onChange={onAutocompleteChange}
      />
      {!areFormsHidden && (
        <>
          <AutocompleteCDS
            disableClearable
            multiple
            required
            containerClassName="mb-24"
            dataTestId={HalcyonTaxTranscriptFormTestIds.FORMS_SELECT}
            disabled={isLoading}
            error={!!validationErrors.forms}
            groupBy={halcyonFormGroupBy}
            helperText={validationErrors.forms}
            label={isPreAuthorization ? 'Pre-Authorized Forms' : 'Forms'}
            name="forms"
            options={halcyonFormOptions}
            value={formsValue}
            onChange={onAutocompleteChange}
          />
          <AutocompleteCDS
            disableClearable
            required
            containerClassName="mb-24"
            dataTestId={HalcyonTaxTranscriptFormTestIds.YEAR_MODE_SELECT}
            disabled={isLoading}
            error={!!validationErrors.yearMode}
            helperText={validationErrors.yearMode}
            label="Year Mode"
            name="yearMode"
            options={halcyonYearModeOptions}
            value={form.yearMode || ''}
            onChange={onAutocompleteChange}
          />
          {isSpecificYears && (
            <AutocompleteCDS
              disableClearable
              multiple
              required
              containerClassName="mb-24"
              dataTestId={HalcyonTaxTranscriptFormTestIds.SPECIFIC_YEARS_SELECT}
              disabled={isLoading}
              error={!!validationErrors.specificYears}
              helperText={validationErrors.specificYears}
              label="Specific Years"
              name="specificYears"
              options={specificYearOptions}
              value={specificYearsValue}
              onChange={onAutocompleteChange}
            />
          )}
        </>
      )}
      <AutocompleteCDS
        disableClearable
        required
        containerClassName="mb-24"
        dataTestId={HalcyonTaxTranscriptFormTestIds.RESUBMIT_MODE_SELECT}
        disabled={isLoading}
        error={!!validationErrors.resubmitMode}
        helperText={validationErrors.resubmitMode}
        isOptionEqualToValue={onGetOptionSelected}
        label="Resubmit Mode"
        name="resubmitMode"
        options={halcyonResubmitModeOptions}
        value={form.resubmitMode || ''}
        onChange={onAutocompleteChange}
      />
      <AutocompleteCDS
        disableClearable
        required
        containerClassName="mb-24"
        dataTestId={HalcyonTaxTranscriptFormTestIds.AUTHORIZATION_SOURCE_SELECT}
        disabled={isLoading}
        error={!!validationErrors.authorizationSource}
        helperText={validationErrors.authorizationSource}
        isOptionEqualToValue={onGetOptionSelected}
        label="Authorization Source"
        name="authorizationSource"
        options={halcyonAuthorizationSourceOptions}
        value={form.authorizationSource || ''}
        onChange={onAutocompleteChange}
      />
      {hasBorrowerOptions && (
        <AutocompleteCDS
          disableClearable
          multiple
          required
          containerClassName="mb-24"
          dataTestId={HalcyonTaxTranscriptFormTestIds.BORROWERS_SELECT}
          disabled={isLoading}
          error={!!validationErrors.borrowers}
          helperText={validationErrors.borrowers}
          label="Borrowers"
          name="borrowers"
          options={borrowerOptions}
          value={borrowersValue}
          onChange={onAutocompleteChange}
        />
      )}
    </ModalFormWrapper>
  );
};

HalcyonTaxTranscriptForm.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(HalcyonTaxTranscriptForm);
